import React from "react";
import { FaArrowRight, FaFacebookF, FaInstagram } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { socialLinks } from "../layout/Footer";

type SocialCard = {
  label: string;
  description: string;
  href: string;
  Icon: React.ComponentType<{ className?: string }>;
  toneClassName: string;
};

const FollowUs: React.FC = () => {
  const { t } = useTranslation();

  const socialCards: SocialCard[] = [
    {
      label: t("Instagram"),
      description: t("See reels, travel ideas, and the latest destination updates."),
      href: socialLinks.instagram,
      Icon: FaInstagram,
      toneClassName: "text-brand-pink bg-brand-pink/10 border-brand-pink/15",
    },
    {
      label: t("Facebook"),
      description: t("Follow us for announcements, offers, and travel inspiration."),
      href: socialLinks.facebook,
      Icon: FaFacebookF,
      toneClassName: "text-blue-600 bg-blue-50 border-blue-100",
    },
  ];

  return (
    <section className="bg-brand-gray py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <span className="inline-flex rounded-full border border-brand-pink/15 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-pink shadow-sm">
              {t("Follow us on")}
            </span>
            <h2 className="mt-4 font-heading text-3xl font-bold text-brand-dark md:text-4xl">
              {t("Stay connected with us")}
            </h2>
          </div>

          <p className="max-w-xl text-sm leading-7 text-gray-600 md:text-base">
            {t(
              "Get destination inspiration, behind-the-scenes moments, and new offers straight from our social channels."
            )}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {socialCards.map((card) => {
            const isPlaceholder = card.href === "#";

            return (
              <a
                key={card.label}
                href={card.href}
                target={isPlaceholder ? undefined : "_blank"}
                rel={isPlaceholder ? undefined : "noreferrer"}
                onClick={(event) => {
                  if (isPlaceholder) {
                    event.preventDefault();
                  }
                }}
                className={`group flex items-center justify-between gap-4 rounded-2xl border border-brand-dark/10 bg-white p-5 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-brand-pink/20 hover:shadow-md ${
                  isPlaceholder ? "cursor-default" : "cursor-pointer"
                }`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-xl border ${card.toneClassName}`}
                  >
                    <card.Icon className="text-lg" />
                  </div>

                  <div className="min-w-0">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-pink">
                      {card.label}
                    </p>
                    <h3 className="mt-1 font-heading text-xl font-bold text-brand-dark">
                      {card.label}
                    </h3>
                    <p className="mt-2 max-w-md text-sm leading-6 text-gray-600">
                      {card.description}
                    </p>
                  </div>
                </div>

                <span
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-gray text-brand-dark transition-colors group-hover:bg-brand-pink group-hover:text-white ${
                    isPlaceholder ? "opacity-70" : ""
                  }`}
                >
                  <FaArrowRight className="text-sm transition-transform group-hover:translate-x-0.5" />
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FollowUs;
